import { getBffUrl, getHealthCheckUrl } from '../config/bff';
import { MockDevXGPTClient } from './mockClient';
import type {
  APIConfig,
  APIError,
  AssistantsResponse,
  ConversationsResponse,
  CreateConversationRequest,
  HealthCheckResponse,
  MessagesResponse,
  SendMessageRequest,
} from './types';

  /**
 * Utility to sanitize JSON strings from backend
 */
function sanitizeJsonString(str: string): string {
  return str
    .replace(/\\n/g, '\\n')
    .replace(/\\'/g, "'")
    .replace(/\\"/g, '\\"')
    .replace(/\\&/g, '&')
    .replace(/\\r/g, '\\r')
    .replace(/\\t/g, '\\t')
    .replace(/\\b/g, '\\b')
    .replace(/\\f/g, '\\f')
    .replace(/â€œ/g, '“')
    .replace(/â€/g, '”')
    .replace(/â€™/g, '’')
    .replace(/â€“/g, '–')
    .replace(/â€”/g, '—')
    .replace(/â€/g, '…');
}

/**
 * DevXGPT API Client
 *
 * Handles all API interactions including:
 * - Fetching conversations
 * - Fetching messages for a conversation
 * - Sending messages with streaming support (SSE)
 * - Creating new conversations
 *
 * Set `useMock: true` in config to use mock data instead of real API
 */
export class DevXGPTClient {
  private config: APIConfig;
  private mockClient?: MockDevXGPTClient;
  private readonly baseUrl: string;

  constructor(config: APIConfig) {
    this.config = config;
    this.baseUrl = getBffUrl();

    if (config.useMock) {
      this.mockClient = new MockDevXGPTClient(config);
      console.log('🎭 Mock mode enabled - Using simulated API responses');
    }
  }

  /**
   * Get authorization headers
   */
  private getHeaders(isEventStream: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: isEventStream ? 'text/event-stream' : 'application/json',
    };

    if (this.config.authToken) {
      headers['Authorization'] = `Bearer ${this.config.authToken}`;
    }

    return headers;
  }

  /**
   * Health check endpoint to verify API connectivity
   * GET /health/check
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    if (this.mockClient) {
      return {
        status: 'ok',
        timestamp: new Date().toISOString(),
        mock: true,
      };
    }

    try {
      const url = getHealthCheckUrl();

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorMessage = `Health check failed: ${response.statusText} (${response.status})`;
        const apiError = new Error(errorMessage) as Error & { status: number };
        apiError.status = response.status;
        throw apiError;
      }

      return await response.json();
    } catch (error) {
      console.error('Health check error:', error);
      throw error;
    }
  }

  /**
   * Fetch all conversations for the user (across all assistants)
   * GET /assistant/all-conversations (or /api/all-conversations via proxy)
   */
  async getConversations(): Promise<ConversationsResponse> {
    // Use mock if enabled
    if (this.mockClient) {
      return this.mockClient.getConversations();
    }

    try {
      const url = `${this.baseUrl}/all-conversations`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorMessage =
          response.status === 401
            ? 'Authentication failed. Please check your token.'
            : response.status === 403
              ? "Access denied. You don't have permission to access this resource."
              : response.status === 500
                ? 'Server error. Please try again later.'
                : `Failed to fetch conversations: ${response.statusText}`;

        const apiError = new Error(errorMessage) as Error & { status: number };
        apiError.status = response.status;
        throw apiError;
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  }

  /**
   * Fetch conversations for a specific assistant
   * GET /assistant/assistant-conversation?assistant_id=XXX
   */
  async getAssistantConversations(assistantId?: string): Promise<ConversationsResponse> {
    // Use mock if enabled
    if (this.mockClient) {
      return this.mockClient.getConversations(assistantId);
    }

    try {
      const params = new URLSearchParams();
      const asstId = assistantId || this.config.assistantId;

      if (!asstId) {
        throw new Error('Assistant ID is required. Please provide an assistantId.');
      }

      params.append('assistant_id', asstId);

      const url = `${this.baseUrl}/assistant-conversation?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch assistant conversations: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching assistant conversations:', error);
      throw error;
    }
  }

  /**
   * Fetch messages for a specific conversation
   * GET /assistant/conversation-messages?assistant_id=XXX&conversation_id=YYY
   */
  async getMessages(conversationId: string, assistantId?: string): Promise<MessagesResponse> {
    // Use mock if enabled
    if (this.mockClient) {
      return this.mockClient.getMessages(conversationId);
    }

    try {
      const params = new URLSearchParams();
      const asstId = assistantId || this.config.assistantId;

      if (!asstId) {
        throw new Error('Assistant ID is required. Please provide an assistantId.');
      }

      params.append('assistant_id', asstId);
      params.append('conversation_id', conversationId);

      const url = `${this.baseUrl}/conversation-messages?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  }

  /**
   * Send a message and receive streaming response
   * POST /assistant/chat (with SSE streaming)
   *
   * Returns conversation_id and thread_id on first message for continuing the conversation
   *
   * @param request - Message data
   * @param onChunk - Callback for each streamed chunk
   * @param onComplete - Callback when streaming completes (includes conversation_id and thread_id)
   * @param onError - Callback for errors
   * @param signal - AbortSignal for cancellation
   */
  // async sendMessageOld(
  //   request: SendMessageRequest,
  //   onChunk: (text: string) => void,
  //   onComplete?: (fullMessage: string, conversationId?: string, threadId?: string) => void,
  //   onError?: (error: APIError) => void,
  //   signal?: AbortSignal
  // ): Promise<void> {
  //   // Use mock if enabled
  //   if (this.mockClient) {
  //     return this.mockClient.sendMessage(request, onChunk, onComplete, onError, signal);
  //   }

  //   // Check if already aborted
  //   if (signal?.aborted) {
  //     const error: APIError = { message: 'Request aborted' };
  //     onError?.(error);
  //     return;
  //   }

  //   try {
  //     // Use user-provided ID, then config ID, then default assistant ID
  //     const asstId = getEffectiveAssistantId(request.assistant_id || this.config.assistantId);

  //     if (!asstId) {
  //       const error: APIError = {
  //         message: 'Assistant ID is required. Please configure VITE_DEVXGPT_DEFAULT_ASSISTANT_ID or provide an assistantId.',
  //       };
  //       onError?.(error);
  //       throw new Error(error.message);
  //     }

  //     const body: any = {
  //       message: request.message,
  //       assistant_id: asstId,
  //     };

  //     // Include conversation_id and thread_id for continuing conversations
  //     if (request.conversation_id) {
  //       body.conversation_id = request.conversation_id;
  //     }

  //     if (request.thread_id) {
  //       body.thread_id = request.thread_id;
  //     }

  //     const response = await fetch(`${this.baseUrl}/chat`, {
  //       method: 'POST',
  //       headers: this.getHeaders(true), // true for event-stream
  //       body: JSON.stringify(body),
  //       signal, // Pass AbortSignal to fetch
  //     });

  //     if (!response.ok) {
  //       const error: APIError = {
  //         message: `Failed to send message: ${response.statusText}`,
  //         status: response.status,
  //       };
  //       onError?.(error);
  //       throw new Error(error.message);
  //     }

  //     // Handle SSE streaming
  //     const reader = response.body?.getReader();
  //     const decoder = new TextDecoder();
  //     let buffer = '';
  //     let fullMessage = '';
  //     let capturedConversationId: string | undefined;
  //     let capturedThreadId: string | undefined;

  //     if (!reader) {
  //       throw new Error('Response body is not readable');
  //     }

  //     while (true) {
  //       // Check if aborted during streaming
  //       if (signal?.aborted) {
  //         reader.cancel();
  //         throw new Error('Request aborted');
  //       }

  //       const { done, value } = await reader.read();

  //       if (done) {
  //         break;
  //       }

  //       // Decode the chunk
  //       buffer += decoder.decode(value, { stream: true });

  //       // Process complete lines
  //       const lines = buffer.split('\n');
  //       buffer = lines.pop() || ''; // Keep incomplete line in buffer

  //       for (const line of lines) {
  //         if (line.startsWith('data: ')) {
  //           try {
  //             const data = JSON.parse(line.substring(6));

  //             // Capture conversation_id and thread_id from any event that contains them
  //             if (data.data?.conversation_id && !capturedConversationId) {
  //               capturedConversationId = data.data.conversation_id;
  //             }
  //             if (data.data?.thread_id && !capturedThreadId) {
  //               capturedThreadId = data.data.thread_id;
  //             }

  //             // Handle message delta events
  //             if (data.event === 'thread.message.delta' && data.data?.delta?.content) {
  //               const content = data.data.delta.content[0];
  //               if (content?.text?.value) {
  //                 const chunk = content.text.value;
  //                 fullMessage += chunk;
  //                 onChunk(chunk);
  //               }
  //             }

  //             // Handle completion
  //             // if (
  //             //   data.event === 'thread.message.completed' ||
  //             //   data.event === 'thread.run.completed'
  //             // ) {
  //             //   onComplete?.(fullMessage, capturedConversationId, capturedThreadId);
  //             // }
  //             if (
  //               data.event === 'thread.run.completed'
  //             ) {
  //               onComplete?.(fullMessage, capturedConversationId, capturedThreadId);
  //             }

  //             // Handle errors
  //             if (data.event === 'thread.run.failed' || data.data?.last_error) {
  //               const error: APIError = {
  //                 message: data.data?.last_error?.message || 'Message failed',
  //                 code: data.data?.last_error?.code,
  //               };
  //               onError?.(error);
  //             }
  //           } catch (e) {
  //             console.warn('Failed to parse SSE data:', line, e);
  //           }
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error sending message:', error);
  //     const apiError: APIError = {
  //       message: error instanceof Error ? error.message : 'Unknown error occurred',
  //     };
  //     onError?.(apiError);
  //     throw error;
  //   }
  // }



/**
 * Enhanced streaming handler for DevXGPT with annotation support
 */
async sendMessage(
  request: SendMessageRequest,
  onChunk: (msg: { 
    text?: string; 
    files?: any[]; 
    id?: number; 
    eventType?: string;
  }) => void,
  onComplete?: (msg: { 
    text?: string; 
    files?: any[]; 
    conversationId?: string; 
    threadId?: string;
  }) => void,
  onError?: (error: APIError) => void,
  signal?: AbortSignal
): Promise<void> {
  if (this.mockClient) {
    return this.mockClient.sendMessage(request, onChunk, onComplete, onError, signal);
  }

  if (signal?.aborted) {
    onError?.({ message: 'Request aborted' });
    return;
  }

  try {
    const asstId = request.assistant_id || this.config.assistantId;
    if (!asstId) {
      const error: APIError = { message: 'Assistant ID is required. Please provide an assistantId.' };
      onError?.(error);
      throw new Error(error.message);
    }

    const body: any = {
      message: request.message,
      assistant_id: asstId,
    };
    if (request.conversation_id) body.conversation_id = request.conversation_id;
    if (request.thread_id) body.thread_id = request.thread_id;

    const response = await fetch(`${this.baseUrl}/chat`, {
      method: 'POST',
      headers: this.getHeaders(true),
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const error: APIError = { message: `Failed to send message: ${response.statusText}`, status: response.status };
      onError?.(error);
      throw new Error(error.message);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullMessage = '';
    let files: any[] = [];
    let capturedConversationId: string | undefined;
    let capturedThreadId: string | undefined;
    let completed = false; // Guard to prevent duplicate completion

    if (!reader) throw new Error('Response body is not readable');

    while (true) {
      if (signal?.aborted) {
        reader.cancel();
        throw new Error('Request aborted');
      }
      const { done, value } = await reader.read();
      if (done) {
        // Don't call onComplete here - wait for the backend's custom thread.message event
        // which is the last event in the stream and contains the files array
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        let parsed: any;
        try {
          parsed = JSON.parse(sanitizeJsonString(line.slice(6)));
        } catch (e) {
          onError?.({ message: 'Malformed SSE data', code: 'PARSE_ERROR' });
          continue;
        }

        // Check nested data object first (most common)
        if (parsed.data?.conversation_id && !capturedConversationId) {
          capturedConversationId = parsed.data.conversation_id;
        }
        if (parsed.data?.thread_id && !capturedThreadId) {
          capturedThreadId = parsed.data.thread_id;
        }
        
        // Check top-level properties (backend custom events)
        if (parsed.thread_id && !capturedThreadId) {
          capturedThreadId = parsed.thread_id;
        }
        if (parsed.conversation_id && !capturedConversationId) {
          capturedConversationId = parsed.conversation_id;
        }

        // thread.message.delta
        if (parsed.event === 'thread.message.delta' && parsed.data?.delta?.content) {
          const contentItem = parsed.data.delta.content[0];
          const content = contentItem?.text?.value;
          
          if (content) {
            fullMessage += content;
            onChunk({ text: content, eventType: parsed.event });
          }
        }
        // message.attachment (files)
        else if (parsed.event === 'message.attachment' && parsed.data?.files) {
          files = parsed.data.files;
          onChunk({ files, eventType: parsed.event });
        }
        // message.delta (generic delta)
        else if (parsed.event === 'message.delta' && parsed.data?.content) {
          fullMessage += parsed.data.content;
          onChunk({ text: parsed.data.content, eventType: parsed.event });
        }
        // final event_type (completion)
        else if (parsed.event_type === 'final' && parsed.data?.response) {
          fullMessage += parsed.data.response;
          onChunk({ text: parsed.data.response, eventType: parsed.event_type });
        }
        // thread.run.completed (completion)
        else if (parsed.event === 'thread.run.completed') {
          // Don't call onComplete yet - wait for the backend's custom thread.message event
          // which contains the conversation_id. This event comes after thread.run.completed.
          console.log('[DevXGPT API Client] thread.run.completed received, waiting for backend thread.message event...');
        }
        // thread.message (backend custom event containing conversation_id and files)
        else if (parsed.event === 'thread.message' && typeof parsed.data === 'object') {
          // This is the final event from the backend that includes conversation_id
          if (parsed.data?.conversation_id && !capturedConversationId) {
            capturedConversationId = parsed.data.conversation_id;
          }
          // Also check for files in the final message
          if (parsed.data?.files && Array.isArray(parsed.data.files)) {
            files = parsed.data.files;
          }
          if (!completed) {
            completed = true;
            onComplete?.({ 
              text: fullMessage, 
              files, 
              conversationId: capturedConversationId, 
              threadId: capturedThreadId
            });
          }
        }
        // thread.run.failed or last_error (error)
        else if (parsed.event === 'thread.run.failed' || parsed.data?.last_error) {
          onError?.({
            message: parsed.data?.last_error?.message || 'Message failed',
            code: parsed.data?.last_error?.code,
          });
        }
      }
    }
  } catch (error) {
    onError?.({ message: error instanceof Error ? error.message : 'Unknown error occurred' });
    throw error;
  }
}

  /**
   * Create a new conversation
   *
   * @alpha This method is reserved for future backend implementation.
   * Currently, new conversations are automatically created by the backend
   * when sending the first message without a conversation_id.
   *
   * @throws {Error} Not yet implemented by backend
   */
  async createConversation(
  request: CreateConversationRequest
): Promise < { conversation_id: string } > {
  // Use mock if enabled
  if(this.mockClient) {
  return this.mockClient.createConversation(request);
}

throw new Error(
  'createConversation is not yet supported by the backend API. ' +
  'New conversations are automatically created when you send the first message. ' +
  'Call sendMessage() without a conversation_id to start a new conversation.'
);
  }

  /**
   * Delete a conversation
   *
   * @alpha This method is reserved for future backend implementation.
   * This feature will likely be needed for privacy/GDPR compliance.
   *
   * @throws {Error} Not yet implemented by backend
   */
  async deleteConversation(conversationId: string): Promise < void> {
  // Use mock if enabled
  if(this.mockClient) {
  return this.mockClient.deleteConversation(conversationId);
}

throw new Error(
  'deleteConversation is not yet supported by the backend API. ' +
  'This feature is planned for a future release to support conversation management and privacy requirements.'
);
  }

  /**
   * Update conversation title
   *
   * @alpha This method is reserved for future backend implementation.
   * This feature will likely be needed for conversation organization.
   *
   * @throws {Error} Not yet implemented by backend
   */
  async updateConversation(conversationId: string, title: string): Promise < void> {
  // Use mock if enabled
  if(this.mockClient) {
  return this.mockClient.updateConversation(conversationId, title);
}

throw new Error(
  'updateConversation is not yet supported by the backend API. ' +
  'This feature is planned for a future release to support conversation renaming and organization.'
);
  }

  /**
   * Fetch available assistants
   * GET /assistant/assistant-details (or /api/assistant-details via proxy)
   */
  async getAssistants(): Promise < AssistantsResponse > {
  // Use mock if enabled
  if(this.mockClient) {
  return this.mockClient.getAssistants();
}

try {
  const url = `${this.baseUrl}/assistant-details`;

  const response = await fetch(url, {
    method: 'GET',
    headers: this.getHeaders(),
  });

  if (!response.ok) {
    const errorMessage =
      response.status === 401
        ? 'Authentication failed. Please check your token.'
        : response.status === 403
          ? "Access denied. You don't have permission to access this resource."
          : response.status === 500
            ? 'Server error. Please try again later.'
            : `Failed to fetch assistants: ${response.statusText}`;

    const apiError = new Error(errorMessage) as Error & { status: number };
    apiError.status = response.status;
    throw apiError;
  }

  return await response.json();
} catch (error) {
  console.error('Error fetching assistants:', error);
  throw error;
}
  }
}

/**
 * Create a DevXGPT API client instance
 */
export function createDevXGPTClient(config: APIConfig): DevXGPTClient {
  return new DevXGPTClient(config);
}
